// CHOISIR SES CRÉNEAUX ET SES FORMATS — en cochant (055).
//
//   · SlotPicker   — les créneaux des prochains jours ouvrés, jour par jour, à
//                    cocher. Avec un agenda branché (Options > Mon agenda),
//                    seuls les créneaux LIBRES apparaissent ; sinon, les heures
//                    de bureau. Un créneau précis s'ajoute à la main.
//   · FormatPicker — ce qu'on propose : sur place, Google Meet, Microsoft
//                    Teams. Plusieurs cochés : la personne choisit le sien.
import { useEffect, useMemo, useState } from 'react';
import { useFreeSlotsQuery, useMyCalendar } from '../calendar/api';
import { Button, Field, Note, Select, Spinner } from '../components/ui';
import { Link } from '../router';
import './invitations.css';

export type Format = 'google_meet' | 'teams' | 'onsite';

export const FORMAT_LABELS: Record<Format, string> = {
  google_meet: 'Google Meet',
  teams: 'Microsoft Teams',
  onsite: 'Sur place',
};

/** Au plus autant de créneaux proposés que le serveur en accepte. */
export const MAX_SLOTS = 30;

const dayKey = (d: Date) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
const timeText = (d: Date) => d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
const dayText = (d: Date) => d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'short' });

/** Sans agenda : les heures pleines de bureau des `days` prochains jours ouvrés. */
function officeHours(days: number, startHour: number, endHour: number, durationMin: number): string[] {
  const out: string[] = [];
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  let worked = 0;
  while (worked < days) {
    d.setDate(d.getDate() + 1);
    if (d.getDay() === 0 || d.getDay() === 6) continue;
    worked += 1;
    for (let h = startHour; h * 60 + durationMin <= endHour * 60; h += 1) {
      const s = new Date(d);
      s.setHours(h, 0, 0, 0);
      out.push(s.toISOString());
    }
  }
  return out;
}

/** Au plus `max` créneaux, pris à intervalles réguliers : toute la période reste couverte. */
function spreadEvenly(list: string[], max: number): string[] {
  if (list.length <= max) return list;
  return Array.from({ length: max }, (_, i) => list[Math.round((i * (list.length - 1)) / (max - 1))]!);
}

export function SlotPicker({
  projectId,
  durationMin,
  value,
  onChange,
}: {
  projectId: string;
  durationMin: number;
  value: string[];
  onChange: (slots: string[]) => void;
}) {
  const calendar = useMyCalendar();
  const connected = Boolean(calendar.data?.configured);
  const [days, setDays] = useState(10);
  const free = useFreeSlotsQuery({ days, startHour: 9, endHour: 18, durationMin, count: 6 }, connected);
  const [custom, setCustom] = useState('');
  // Par défaut, on ne coche rien : chaque personne choisit elle-même parmi vos
  // créneaux libres. Cocher à la main reste possible.
  const [mode, setMode] = useState<'auto' | 'manual'>('auto');

  // Les candidats : libres d'après l'agenda (au quart d'heure près, on garde
  // les heures et demi-heures), sinon les heures de bureau.
  const candidates = useMemo(() => {
    if (connected) return free.data?.free ?? [];
    return officeHours(days, 9, 18, durationMin);
  }, [connected, free.data, days, durationMin]);

  // Mode « ils choisissent » : tous les créneaux libres de la période sont
  // proposés (répartis si trop nombreux) — sans rien cocher.
  const auto = useMemo(() => spreadEvenly(candidates, MAX_SLOTS), [candidates]);
  const loading = connected && free.isPending;
  useEffect(() => {
    if (mode !== 'auto' || loading) return;
    if (auto.join() !== value.join()) onChange(auto);
  }, [mode, loading, auto, value, onChange]);

  const chosen = new Set(value.map((v) => new Date(v).toISOString()));
  const toggle = (iso: string) => {
    if (chosen.has(iso)) onChange(value.filter((v) => new Date(v).toISOString() !== iso));
    else if (value.length < MAX_SLOTS) onChange([...value, iso].sort());
  };

  // Regroupés par jour, avec les créneaux cochés hors liste (ajoutés à la main).
  const groups = useMemo(() => {
    const all = [...new Set([...candidates, ...value.map((v) => new Date(v).toISOString())])].sort();
    const map = new Map<string, { label: string; slots: string[] }>();
    for (const iso of all) {
      const d = new Date(iso);
      const k = dayKey(d);
      if (!map.has(k)) map.set(k, { label: dayText(d), slots: [] });
      map.get(k)!.slots.push(iso);
    }
    return [...map.values()];
  }, [candidates, value]);

  if (calendar.isPending) return <Spinner label="Lecture de votre agenda" />;

  const modeSwitch = (
    <div className="slot-mode" role="radiogroup" aria-label="Qui choisit le créneau ?">
      <label className={`slot-mode-opt${mode === 'auto' ? ' slot-mode-on' : ''}`}>
        <input className="zy-radio" type="radio" name="slot-mode" checked={mode === 'auto'} onChange={() => setMode('auto')} />
        <span>
          <strong>Chaque personne choisit dans mon agenda</strong>
          <span>Vos créneaux libres sont proposés ; chacun prend le sien.</span>
        </span>
      </label>
      <label className={`slot-mode-opt${mode === 'manual' ? ' slot-mode-on' : ''}`}>
        <input className="zy-radio" type="radio" name="slot-mode" checked={mode === 'manual'} onChange={() => setMode('manual')} />
        <span>
          <strong>Je choisis les créneaux</strong>
          <span>Vous cochez ceux à proposer.</span>
        </span>
      </label>
    </div>
  );

  if (mode === 'auto') {
    return (
      <div className="slot-picker">
        {modeSwitch}
        <div className="slot-bar">
          <span>
            {connected
              ? <>Vos créneaux <strong>libres</strong> d’après votre {calendar.data?.host}, de 9 h à 18 h, sur</>
              : <>Sans agenda branché, les heures de bureau (9 h – 18 h) — <Link to={`/projet/${encodeURIComponent(projectId)}/options`} className="card-title-link">branchez votre agenda</Link> pour ne proposer que vos créneaux libres. Sur</>}
          </span>
          <Select aria-label="Sur combien de jours" value={days} onChange={(e) => setDays(Number(e.target.value))}>
            {[5, 10, 15, 20].map((n) => <option key={n} value={n}>{n} jours ouvrés</option>)}
          </Select>
        </div>
        {loading ? <Spinner label="Recherche de vos créneaux libres" /> : null}
        {free.error ? <p className="inv-sub inv-sub-error">Agenda illisible pour le moment : les heures de bureau sont proposées.</p> : null}
        {!loading ? (
          <Note>
            {value.length === 0
              ? 'Aucun créneau libre sur cette période : allongez-la.'
              : <>
                  <strong>{value.length}</strong> créneau{value.length > 1 ? 'x' : ''} libre{value.length > 1 ? 's' : ''} seront proposés
                  {candidates.length > MAX_SLOTS ? ` (répartis sur la période, ${MAX_SLOTS} au plus)` : ''}. Chaque personne
                  choisit le sien sur son lien ; un créneau pris disparaît pour les autres, et un créneau devenu occupé dans
                  votre agenda n’est plus proposé.
                </>}
          </Note>
        ) : null}
      </div>
    );
  }

  return (
    <div className="slot-picker">
      {modeSwitch}
      <div className="slot-bar">
        <span>
          {connected
            ? <>Vos créneaux <strong>libres</strong> d’après votre {calendar.data?.host}</>
            : <>Heures de bureau — <Link to={`/projet/${encodeURIComponent(projectId)}/options`} className="card-title-link">branchez votre agenda</Link> pour ne voir que vos créneaux libres</>}
        </span>
        <Select aria-label="Sur combien de jours" value={days} onChange={(e) => setDays(Number(e.target.value))}>
          {[5, 10, 15, 20].map((n) => <option key={n} value={n}>{n} jours ouvrés</option>)}
        </Select>
        {connected && free.data?.proposed.length ? (
          <Button variant="link" size="s" className="u-push-end" type="button" onClick={() => onChange(free.data!.proposed)}>
            Proposer {free.data.proposed.length} créneaux répartis
          </Button>
        ) : null}
        {value.length > 0 ? (
          <Button variant="link" size="s" className="u-push-end" type="button" onClick={() => onChange([])}>Tout décocher</Button>
        ) : null}
      </div>

      {connected && free.isPending ? <Spinner label="Recherche de vos créneaux libres" /> : null}
      {free.error ? <p className="inv-sub inv-sub-error">Agenda illisible pour le moment : les heures de bureau sont proposées.</p> : null}

      <div className="slot-days">
        {groups.map((g) => (
          <div key={g.label} className="slot-day">
            <span className="slot-day-label">{g.label}</span>
            <span className="slot-chips">
              {g.slots.map((iso) => {
                const on = chosen.has(iso);
                return (
                  <label key={iso} className={`slot-chip${on ? ' slot-chip-on' : ''}`}>
                    <input className="zy-check" type="checkbox" checked={on} onChange={() => toggle(iso)} disabled={!on && value.length >= MAX_SLOTS} />
                    {timeText(new Date(iso))}
                  </label>
                );
              })}
            </span>
          </div>
        ))}
        {groups.length === 0 && !free.isPending ? <p className="zy-texte-muet">Aucun créneau libre sur cette période.</p> : null}
      </div>

      <div className="slot-custom">
        <Field
          label="Ajouter un créneau précis"
          type="datetime-local"
          voice={false}
          value={custom}
          onChange={(e) => setCustom(e.target.value)}
        />
        <Button variant="link" size="s" className="u-push-end"
          type="button"
          disabled={!custom || Number.isNaN(new Date(custom).getTime())}
          onClick={() => { toggle(new Date(custom).toISOString()); setCustom(''); }}
        >
          Ajouter
        </Button>
      </div>
      <Note>
        <strong>{value.length}</strong> créneau{value.length > 1 ? 'x' : ''} coché{value.length > 1 ? 's' : ''}
        {value.length >= MAX_SLOTS ? ` (${MAX_SLOTS} au plus)` : ''}. Chaque personne choisit le sien sur son lien ; un
        créneau pris disparaît pour les autres.
      </Note>
    </div>
  );
}

export function FormatPicker({
  value,
  onChange,
  location,
  onLocation,
}: {
  value: Format[];
  onChange: (f: Format[]) => void;
  location: string;
  onLocation: (v: string) => void;
}) {
  const toggle = (f: Format) => onChange(value.includes(f) ? value.filter((x) => x !== f) : [...value, f]);
  return (
    <div className="format-picker">
      <div className="format-options">
        {(Object.keys(FORMAT_LABELS) as Format[]).map((f) => (
          <label key={f} className={`slot-chip format-chip${value.includes(f) ? ' slot-chip-on' : ''}`}>
            <input className="zy-check" type="checkbox" checked={value.includes(f)} onChange={() => toggle(f)} />
            {FORMAT_LABELS[f]}
          </label>
        ))}
      </div>
      {value.includes('onsite') ? (
        <Field
          label="Lieu du rendez-vous *"
          value={location}
          onChange={(e) => onLocation(e.target.value)}
          placeholder="Atelier de Lyon, bâtiment B, accueil"
          hint="Ce que la personne lira pour vous trouver."
        />
      ) : null}
      <Note>
        {value.length > 1
          ? 'Plusieurs formats cochés : la personne choisit le sien avec son créneau. Le lien Meet ou Teams est créé selon son choix.'
          : value.length === 1
            ? `Tout le monde en ${FORMAT_LABELS[value[0]!].toLowerCase()}.`
            : 'Cochez au moins un format.'}
      </Note>
    </div>
  );
}
